import { useMemo, useState } from "react";
import {
  Box,
  Alert,
  Button,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import SearchIcon from "@mui/icons-material/Search";
import FolderIcon from "@mui/icons-material/Folder";
import PageContainer from "../page-container/page-container";
import FolderListTable from "./components/folder-list-table";
import FolderIconGrid from "./components/folder-icon-grid";
import FolderFilesView from "./components/folder-files-view";
import ViewToggle, { FileExplorerViewMode } from "./components/view-toggle";
import LoadingScreen from "../progress/loading-screen";
import { useFileExplorerLogic } from "./hooks/use-file-explorer-logic";
import { useFileActions } from "./hooks/use-file-actions";
import { filterFoldersByName } from "./utils/filters";
import {
  getStoredFolderViewMode,
  setStoredFolderViewMode,
} from "./utils/view-mode-storage";

const FileExplorer = () => {
  const {
    selectedFolder,
    selectedFolderName,
    isFolderNotFound,
    folders,
    isLoading,
    error,
    folderBlobs,
    isLoadingMoreFiles,
    loadMoreFilesError,
    expectedFileCount,
    handleRetryLoadingFiles,
    handleViewFolder,
    handleBackToList,
    handleRefresh,
  } = useFileExplorerLogic();

  const {
    onDeleteFile,
    onDeleteFiles,
    onDownloadFile,
    onRehydrateFile,
    onRehydrateFiles,
  } = useFileActions();

  const [folderSearch, setFolderSearch] = useState("");
  const [folderViewMode, setFolderViewModeState] =
    useState<FileExplorerViewMode>(getStoredFolderViewMode);

  const setFolderViewMode = (mode: FileExplorerViewMode) => {
    setFolderViewModeState(mode);
    setStoredFolderViewMode(mode);
  };

  const filteredFolders = useMemo(
    () => filterFoldersByName(folders ?? [], folderSearch),
    [folders, folderSearch],
  );

  if (isLoading) {
    return <LoadingScreen />;
  }

  if (error) {
    return (
      <Box sx={{ flexGrow: 1 }}>
        <Alert severity="error">Unable to load file information</Alert>
      </Box>
    );
  }

  if (isFolderNotFound) {
    return (
      <Box sx={{ flexGrow: 1 }}>
        <Alert
          severity="warning"
          action={
            <Button color="inherit" size="small" onClick={handleBackToList}>
              Back to Folders
            </Button>
          }
        >
          Folder &quot;{selectedFolderName}&quot; was not found. It may have
          been removed.
        </Alert>
      </Box>
    );
  }

  return (
    <PageContainer
      title="File Explorer"
      maxHeight="900px"
      actions={
        <Tooltip title="Refresh">
          <IconButton onClick={handleRefresh} size="small" aria-label="Refresh">
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      }
    >
      <Box sx={{ px: 2 }}>
        {selectedFolder ? (
          <FolderFilesView
            folder={selectedFolder}
            blobs={folderBlobs}
            onBackToList={handleBackToList}
            onDeleteFile={(blob) => onDeleteFile(blob, handleRefresh)}
            onDownloadFile={onDownloadFile}
            onRehydrateFile={(blob) => onRehydrateFile(blob, handleRefresh)}
            onDeleteFiles={(blobs) => onDeleteFiles(blobs, handleRefresh)}
            onRehydrateFiles={(blobs) => onRehydrateFiles(blobs, handleRefresh)}
            isLoadingMore={isLoadingMoreFiles}
            expectedFileCount={expectedFileCount}
            loadMoreError={loadMoreFilesError}
            onRetryLoading={handleRetryLoadingFiles}
          />
        ) : (
          <>
            <Box
              sx={{
                mb: 3,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                flexWrap: "wrap",
                gap: 2,
                backgroundColor: "action.hover",
                borderRadius: 2,
                p: 2,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <FolderIcon sx={{ color: "primary.main", fontSize: 28 }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Folders
                </Typography>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  flexWrap: "wrap",
                }}
              >
                <TextField
                  size="small"
                  placeholder="Search folders"
                  value={folderSearch}
                  onChange={(e) => setFolderSearch(e.target.value)}
                  slotProps={{
                    input: {
                      startAdornment: (
                        <SearchIcon
                          fontSize="small"
                          sx={{ mr: 1, color: "text.secondary" }}
                        />
                      ),
                    },
                  }}
                  sx={{ width: 220 }}
                />
                <ViewToggle
                  value={folderViewMode}
                  onChange={setFolderViewMode}
                />
              </Box>
            </Box>
            {folderViewMode === "list" ? (
              <FolderListTable
                folders={filteredFolders}
                onViewFolder={handleViewFolder}
              />
            ) : (
              <FolderIconGrid
                folders={filteredFolders}
                onViewFolder={handleViewFolder}
              />
            )}
          </>
        )}
      </Box>
    </PageContainer>
  );
};

export default FileExplorer;
